import { CheckBox } from '@design';
import * as Radix from '@radix-ui/react-dropdown-menu';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import clsx from 'clsx';
import React, { CSSProperties, ReactNode } from 'react';

import styles from './Filter.module.scss';

export type ItemDirection = 'column' | 'row';

export interface OptionProps {
  option: FilterOption;
  disabled?: boolean;
  multiselect?: boolean;
  isChecked?: boolean;
  disabledTooltipLabel?: string;
  handleClick: () => void;
  direction?: ItemDirection;
  indentedDescription?: number;
  renderCustomFilterOptionLabel?: (filterOption: FilterOption) => ReactNode;
  dataTestId?: string;
  'data-testid'?: string;
}

export const Option = ({
  option,
  disabled,
  multiselect,
  isChecked,
  disabledTooltipLabel,
  handleClick,
  direction = 'column',
  indentedDescription = 0,
  renderCustomFilterOptionLabel,
  dataTestId,
  'data-testid': dataTestIdAttr,
}: OptionProps) => {
  const resolvedTestId =
    dataTestIdAttr ??
    dataTestId ??
    (multiselect ? 'multiselect-item' : 'single-item');

  const itemTextClass =
    direction === 'row' ? styles.itemTextRow : styles.itemText;

  if (multiselect) {
    return (
      <TooltipContainer label={disabledTooltipLabel}>
        <Radix.CheckboxItem
          className={styles.checkboxItem}
          onClick={e => {
            e.preventDefault();

            if (!disabled && !disabledTooltipLabel) {
              handleClick();
            }
          }}
          disabled={!!disabledTooltipLabel}
        >
          <div
            className={itemTextClass}
            data-item-text
            data-testid={resolvedTestId}
          >
            <CheckBox
              label={
                <span
                  className={
                    disabled ? styles.itemLabelLocked : styles.itemLabel
                  }
                >
                  {renderCustomFilterOptionLabel?.(option) ?? option.name}
                </span>
              }
              name={option.key}
              checked={isChecked}
              variant={disabled ? 'disabled' : 'primary'}
            />
          </div>
        </Radix.CheckboxItem>
      </TooltipContainer>
    );
  }

  return (
    <Radix.Item
      className={clsx(styles.item, {
        [styles.itemChecked]: isChecked,
      })}
      onSelect={e => {
        if (option.childName) {
          e.preventDefault();
        }

        handleClick();
      }}
      disabled={disabled}
    >
      <span className={styles.menuItemLeftIndicator} data-menu-item-indicator />
      <div
        className={itemTextClass}
        data-item-text
        data-testid={resolvedTestId}
      >
        <span className={disabled ? styles.itemLabelLocked : styles.itemLabel}>
          {renderCustomFilterOptionLabel?.(option) ?? option.name}
        </span>
        <span
          className={styles.itemDescription}
          data-item-description
          style={
            indentedDescription
              ? ({ '--indent': `${indentedDescription}px` } as CSSProperties)
              : undefined
          }
        >
          {option.description}
        </span>
      </div>
    </Radix.Item>
  );
};
